import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { CalendarClock, AlertTriangle, ExternalLink, RefreshCw, Trash2, Info as InfoIcon, BarChart3 } from "lucide-react";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

const emptyForm = {
  hearing_date: "", hearing_time: "", timezone: "America/New_York",
  hearing_type: "", judge: "", courthouse: "", courtroom: "",
  appearance_mode: "in_person", remote_instructions: "",
  official_calendar_url: "", notes: "", source: "manual",
};

function reminderChips(hearingDateISO, hearingTime = "") {
  if (!hearingDateISO) return [];
  const dt = new Date(`${hearingDateISO}T${hearingTime || "09:00"}:00`);
  const now = new Date();
  const ms = dt.getTime() - now.getTime();
  const days = ms / 86400000;
  const hours = ms / 3600000;
  const list = [];
  if (days > 7) list.push({ label: "7-day reminder", tone: "border-slate-200 text-slate-600" });
  else if (days > 1) list.push({ label: "7-day reminder ✓", tone: "border-emerald-300 bg-emerald-50 text-emerald-800" });
  if (hours > 24) list.push({ label: "24-hour reminder", tone: "border-slate-200 text-slate-600" });
  else if (hours > 2) list.push({ label: "24-hour ✓", tone: "border-emerald-300 bg-emerald-50 text-emerald-800" });
  if (hours > 2) list.push({ label: "2-hour reminder", tone: "border-slate-200 text-slate-600" });
  else if (hours > 0) list.push({ label: "2-hour ✓", tone: "border-amber-300 bg-amber-50 text-amber-800" });
  if (hours <= 0) list.push({ label: "Past hearing", tone: "border-red-300 bg-red-50 text-red-800" });
  return list;
}

export default function CourtDatesPanel({ caseId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/cases/${caseId}/court-dates`);
      setItems(r.data);
    } catch (e) {
      toast.error("Could not load court dates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (caseId) load(); /* eslint-disable-next-line */ }, [caseId]);

  const save = async () => {
    if (!form.hearing_date) { toast.error("Hearing date is required"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/cases/${caseId}/court-dates`, form);
      setItems((prev) => [...prev, r.data].sort((a, b) => a.hearing_date.localeCompare(b.hearing_date)));
      setForm(emptyForm);
      setShowForm(false);
      toast.success("Court date saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const markChecked = async (id) => {
    try {
      const r = await api.post(`/cases/${caseId}/court-dates/${id}/checked`);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, last_checked_at: r.data.last_checked_at } : x)));
      toast.success("Marked as re-checked");
    } catch (e) { toast.error("Could not mark checked"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this court date?")) return;
    try {
      await api.delete(`/cases/${caseId}/court-dates/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { toast.error("Could not delete"); }
  };

  return (
    <section className="border border-slate-200 p-6 md:p-8" data-testid="court-dates-panel">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-slate-700" />
          <h3 className="font-serif-display text-xl text-slate-900">Court dates &amp; hearings</h3>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white text-xs h-8" data-testid="add-court-date-btn">
          {showForm ? "Cancel" : "Add hearing"}
        </Button>
      </div>

      <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2 mb-4" data-testid="court-date-warning">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
        <span>Court dates, hearing times, courtrooms, and remote-access instructions may change. Always confirm your appearance information using the latest court notice, official docket, or clerk&apos;s office before the hearing.</span>
      </div>

      {showForm && (
        <div className="border border-slate-200 p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="court-date-form">
          <Field label="Hearing date *"><Input type="date" value={form.hearing_date} onChange={(e) => setForm({ ...form, hearing_date: e.target.value })} className={inputCls} data-testid="cd-date" /></Field>
          <Field label="Start time"><Input type="time" value={form.hearing_time} onChange={(e) => setForm({ ...form, hearing_time: e.target.value })} className={inputCls} data-testid="cd-time" /></Field>
          <Field label="Time zone"><Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className={inputCls} placeholder="America/New_York" /></Field>
          <Field label="Hearing type"><Input value={form.hearing_type} onChange={(e) => setForm({ ...form, hearing_type: e.target.value })} className={inputCls} placeholder="Motion, status, trial, etc." /></Field>
          <Field label="Judge"><Input value={form.judge} onChange={(e) => setForm({ ...form, judge: e.target.value })} className={inputCls} /></Field>
          <Field label="Courthouse"><Input value={form.courthouse} onChange={(e) => setForm({ ...form, courthouse: e.target.value })} className={inputCls} /></Field>
          <Field label="Courtroom"><Input value={form.courtroom} onChange={(e) => setForm({ ...form, courtroom: e.target.value })} className={inputCls} /></Field>
          <Field label="Appearance mode">
            <select value={form.appearance_mode} onChange={(e) => setForm({ ...form, appearance_mode: e.target.value })} className={`${inputCls} h-10 px-3 w-full`}>
              <option value="in_person">In-person</option>
              <option value="phone">Telephone</option>
              <option value="video">Video</option>
            </select>
          </Field>
          <Field label="Official calendar / docket URL"><Input value={form.official_calendar_url} onChange={(e) => setForm({ ...form, official_calendar_url: e.target.value })} className={inputCls} placeholder="https://…" /></Field>
          <div className="md:col-span-3">
            <Field label="Remote-access instructions (verbatim from the court notice)"><Input value={form.remote_instructions} onChange={(e) => setForm({ ...form, remote_instructions: e.target.value })} className={inputCls} /></Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></Field>
          </div>
          <div className="md:col-span-3 flex gap-2">
            <Button onClick={save} disabled={saving} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid="cd-save-btn">
              {saving ? "Saving…" : "Save hearing"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="label-tiny">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm" data-testid="court-dates-empty">
          No court dates saved yet.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="court-dates-list">
          {items.map((cd) => (
            <li key={cd.id} className="border border-slate-200 p-4" data-testid={`court-date-${cd.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-serif-display text-lg text-slate-900">
                    {cd.hearing_date}{cd.hearing_time ? ` · ${cd.hearing_time}` : ""}
                    {cd.timezone ? <span className="text-xs text-slate-500 ml-2 font-sans">{cd.timezone}</span> : null}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {[cd.hearing_type, cd.judge && `Judge ${cd.judge}`, cd.courthouse, cd.courtroom && `Room ${cd.courtroom}`]
                      .filter(Boolean).join(" · ")}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {reminderChips(cd.hearing_date, cd.hearing_time).map((c, i) => (
                      <Badge key={i} variant="outline" className={`rounded-none font-mono-tech text-[10px] ${c.tone}`}>
                        {c.label}
                      </Badge>
                    ))}
                    <Badge variant="outline" className="rounded-none border-slate-300 text-[10px] font-mono-tech">
                      {cd.appearance_mode?.replace("_", "-")}
                    </Badge>
                    <Badge variant="outline" className="rounded-none border-slate-300 text-[10px] font-mono-tech">
                      Source: {cd.source}
                    </Badge>
                  </div>
                  {cd.remote_instructions && <div className="mt-2 text-xs text-slate-700"><span className="label-tiny">Remote:</span> {cd.remote_instructions}</div>}
                  {cd.notes && <div className="mt-1 text-xs text-slate-600">{cd.notes}</div>}
                  <div className="text-[11px] text-slate-500 mt-2">
                    Last checked: {cd.last_checked_at ? new Date(cd.last_checked_at).toLocaleString() : "—"}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {cd.official_calendar_url && (
                    <a href={cd.official_calendar_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 text-xs text-slate-900 hover:underline underline-offset-4"
                       data-testid={`open-docket-${cd.id}`}>
                      Open official docket <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <Button size="sm" variant="outline" onClick={() => markChecked(cd.id)}
                          className="rounded-none border-slate-300 h-7 text-[11px]" data-testid={`mark-checked-${cd.id}`}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Just re-checked
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(cd.id)}
                          className="rounded-none text-red-700 hover:bg-red-50 h-7 text-[11px]" data-testid={`delete-cd-${cd.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <JudgeInsights judge={cd.judge} courthouse={cd.courthouse} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function JudgeInsights({ judge, courthouse }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasFilter = Boolean((judge || "").trim() || (courthouse || "").trim());

  useEffect(() => {
    if (!hasFilter || !expanded) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/court-insights", {
          params: { judge: judge || undefined, courthouse: courthouse || undefined },
        });
        if (active) setData(r.data);
      } catch (e) {
        if (active) setData({ status: "error" });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [judge, courthouse, hasFilter, expanded]);

  if (!hasFilter) return null;

  return (
    <div className="mt-4 border-t border-dashed border-slate-200 pt-3" data-testid={`judge-insights-${(judge || courthouse || "unknown").toLowerCase().replace(/\s+/g, "-")}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-wider text-slate-600 hover:text-slate-900"
        data-testid="judge-insights-toggle"
      >
        <BarChart3 className="w-3.5 h-3.5" />
        {expanded ? "Hide" : "Show"} anonymized courtroom insights
      </button>

      {expanded && (
        <div className="mt-3">
          {loading ? (
            <div className="text-xs text-slate-500">Loading insights…</div>
          ) : !data ? null : data.status === "insufficient_data" ? (
            <div className="border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 flex items-start gap-2">
              <InfoIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
              <div>
                <span className="font-medium">Not enough data yet</span> to publish anonymized stats for this
                courtroom. We suppress insights until at least {data.anonymity_floor} hearings from multiple
                distinct users are on file.
              </div>
            </div>
          ) : data.status === "ok" ? (
            <div className="border border-slate-200 bg-slate-50 p-4 text-xs text-slate-800">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-900">
                  {data.judge ? `Judge ${data.judge}` : ""}
                  {data.judge && data.courthouse ? " · " : ""}
                  {data.courthouse}
                </span>
                <Badge variant="outline" className="rounded-none text-[10px] font-mono-tech border-slate-300 ml-auto">
                  n = {data.sample_size} · {data.distinct_users} users
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="label-tiny mb-1.5">Most common hearing types</div>
                  <ul className="space-y-1">
                    {data.top_hearing_types.map((t, i) => (
                      <li key={i} className="flex items-center gap-2" data-testid={`insight-type-${i}`}>
                        <span className="flex-1 truncate capitalize">{t.type || "unspecified"}</span>
                        <span className="tabular-nums text-slate-500">{t.share}%</span>
                        <div className="w-24 h-1 bg-slate-200 relative">
                          <div className="absolute inset-y-0 left-0 bg-slate-900" style={{ width: `${t.share}%` }} />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="label-tiny mb-1.5">Appearance modes observed</div>
                  <ul className="space-y-1">
                    {data.top_appearance_modes.map((m, i) => (
                      <li key={i} className="flex items-center gap-2" data-testid={`insight-mode-${i}`}>
                        <span className="flex-1 truncate">{(m.mode || "unspecified").replace("_", "-")}</span>
                        <span className="tabular-nums text-slate-500">{m.share}%</span>
                        <div className="w-24 h-1 bg-slate-200 relative">
                          <div className="absolute inset-y-0 left-0 bg-orange-600" style={{ width: `${m.share}%` }} />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>{data.recent_share_pct}% of observations from the last 12 months.</span>
                <span aria-hidden="true">·</span>
                <span>Aggregate, anonymized data from ProSe.Claims users. Not affiliated with the court.</span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500">Insights unavailable.</div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-tiny">{label}</Label>
      {children}
    </div>
  );
}
